import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import React, { Fragment, useState } from 'react';
import ExpandAccordion from '../../products/components/productForm/ExpandAccordion';
import { ISection } from '../types/interfaces';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import { useTranslation } from 'react-i18next';
import { FieldArrayRenderProps, getIn, useFormikContext } from 'formik';
import { customStyles } from '../../../utils/SelectStyles';
import FormUpload from '../../../components/formInputs/FormUpload';
import FormGalleryUpload from '../../../components/formInputs/FormGalleryUpload';
import PageSubSections from './PageSubSections';
import ModalContainer from '../../../components/ModalContainer';
import DeleteModal from '../../../components/DeleteModal';
import { deleteIcon } from '../../../config/variables';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import SectionHeader from '../../../components/SectionHeader';
import PageSectionModels from './PageSectionModels';

export default function PageSection({
  data,
  expanded,
  setExpanded,
  index,
  namePref,
  subSection,
  arrayHelpers
}: {
  data: ISection;
  expanded: number;
  setExpanded: React.Dispatch<React.SetStateAction<number>>;
  index: number;
  namePref?: string;
  subSection?: boolean;
  arrayHelpers?: FieldArrayRenderProps;
}) {
  const formik = useFormikContext<{
    sections: ISection[];
  }>();
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const sectionName = `${namePref ? `${namePref}.` : ''}${subSection ? 'sub_sections' : 'sections'}[${index}]`;
  const sectionData = getIn(formik.values, `${sectionName}`);

  const deleteRoute = '';

  const sectionTypeOptions = [
    {
      label: t("text"),
      value: 'text'
    },
    {
      label: t("image"),
      value: 'image'
    },
    {
      label: t("gallery"),
      value: 'gallery'
    },
    {
      label: t("video"),
      value: 'video'
    },
    {
      label: t("text_with_icon"),
      value: 'text_with_icon'
    },
    {
      label: t("text_with_image"),
      value: 'text_with_image'
    },
    {
      label: t("text_with_gallery"),
      value: 'text_with_gallery'
    },
    {
      label: t("text_with_video"),
      value: 'text_with_video'
    },
    {
      label: t("text_with_image_and_icon"),
      value: 'text_with_image_and_icon'
    }
  ];

  return (
    <Fragment>
      <Accordion
        expanded={expanded === index}
        onChange={(e) => {
          e.stopPropagation();
          setExpanded(expanded === index ? -1 : index);
        }}
      >
        <AccordionSummary
          sx={{
            '&.Mui-focusVisible': {
              backgroundColor: '#000'
            }
          }}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={`header-container ${expanded !== index && 'expand'}`}
        >
          <div className="header-content">
            <p>
              <ExpandAccordion expand={expanded === index} /> {data?.name || `${subSection ? 'Sub section' : 'Section'} ${index + 1}`}
            </p>
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (sectionData?.id) {
                  setDeleteModalOpen(true);
                } else {
                  arrayHelpers?.remove(index);
                }
              }}
            >
              {deleteIcon}
            </span>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <SectionHeader title={t('section_data')}>
            <div className="inputs_group">
              <FieldWrapper title={t('name')} inputName={`${sectionName}.name`} inputPlaceholder={t('name')} input customPadding />
            </div>
            <div className="inputs_group">
              <FieldWrapper title={t('order')} inputName={`${sectionName}.order`} inputPlaceholder={t('order')} input customPadding />
              <FieldWrapper
                customPadding
                title={t('section_type')}
                inputPlaceholder={t(`section_type`)}
                inputName={`${sectionName}.type`}
                options={sectionTypeOptions}
                selectStyle={customStyles}
                defaultValue={sectionTypeOptions?.find((section) => section.value === sectionData?.type)}
                select
                formik={formik}
              />
            </div>
            {sectionData?.type?.includes('text') && (
              <Fragment>
                <div className="inputs_group">
                  <FieldWrapper title={t('name_en')} inputName={`${sectionName}.content.title.en`} inputPlaceholder={t('name_en')} input customPadding />
                  <FieldWrapper title={t('name_ar')} inputName={`${sectionName}.content.title.ar`} inputPlaceholder={t('name_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('subtitle_en')} inputName={`${sectionName}.content.subtitle.en`} inputPlaceholder={t('subtitle_en')} input customPadding />
                  <FieldWrapper title={t('subtitle_ar')} inputName={`${sectionName}.content.subtitle.ar`} inputPlaceholder={t('subtitle_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('description_en')} inputName={`${sectionName}.content.description.en`} inputPlaceholder={t('description_en')} textArea customPadding />
                  <FieldWrapper title={t('description_ar')} inputName={`${sectionName}.content.description.ar`} inputPlaceholder={t('description_ar')} textArea customPadding />
                </div>
              </Fragment>
            )}
            {sectionData?.type?.includes('image') && (
              <Fragment>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload formik={formik} name={`${sectionName}.media[0].url`} label={t('image_desktop')} />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FormUpload formik={formik} name={`${sectionName}.media[0].url_mobile`} label={t('image_mobile')} />
                  </FieldWrapper>
                  {sectionData?.type?.includes('icon') && (
                    <FieldWrapper>
                      <FormUpload formik={formik} name={`${sectionName}.media[0].url`} label={t('icon')} />
                    </FieldWrapper>
                  )}
                </div>
              </Fragment>
            )}
            {sectionData?.type?.includes('icon') && !sectionData?.type?.includes('image') && (
              <Fragment>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload formik={formik} name={`${sectionName}.media[0].url`} label={t('icon')} />
                  </FieldWrapper>
                  <FieldWrapper />
                </div>
              </Fragment>
            )}
            {sectionData?.type?.includes('gallery') && (
              <Fragment>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormGalleryUpload formik={formik} name={`${sectionName}.media`} label={t('gallery')} />
                  </FieldWrapper>
                </div>
              </Fragment>
            )}
            {sectionData?.type?.includes('video') && (
              <Fragment>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload formik={formik} name={`${sectionName}.video.url`} video label={t('video_desktop')} />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FormUpload formik={formik} name={`${sectionName}.video.poster_url`} label={t('video_desktop_poster')} />
                  </FieldWrapper>
                </div>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload formik={formik} name={`${sectionName}.video.url_mobile`} video label={t('video_mobile')} />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FormUpload formik={formik} name={`${sectionName}.video.poster_mobile_url`} label={t('video_mobile_poster')} />
                  </FieldWrapper>
                </div>
              </Fragment>
            )}
          </SectionHeader>
          <PageSectionModels subSection={subSection} hasRelation={sectionData?.has_relation} sectionName={sectionName} />
          <SectionHeader title={t("button_data")} customStyle={subSection ? "last noMargin": ""}>
            <div className="inputs_group" style={{ marginBottom: subSection ? "0" : '2rem' }}>
              <div className="switch_container">
                <SwitchActivation
                  onChange={(value: any) => formik.setFieldValue(`${sectionName}.has_button`, value?.target?.checked ? 1 : 0)}
                  active={sectionData.has_button === 1}
                />
                <p>{t('has_button')}</p>
              </div>
            </div>
            {!!sectionData?.has_button && (
              <Fragment>
                <div className="inputs_group">
                  <FieldWrapper
                    customPadding
                    title={t('button_type')}
                    inputPlaceholder={t(`button_type`)}
                    inputName={`${sectionName}.button_text`}
                    options={sectionTypeOptions}
                    selectStyle={customStyles}
                    defaultValue={sectionTypeOptions?.find((section) => section.value === sectionData?.button_text)}
                    select
                    formik={formik}
                  />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('button_text_en')} inputName={`${sectionName}.button_text.en`} inputPlaceholder={t('button_text_en')} input customPadding />
                  <FieldWrapper title={t('button_text_ar')} inputName={`${sectionName}.button_text.ar`} inputPlaceholder={t('button_text_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('button_data')} noPadding={subSection} inputName={`${sectionName}.button_data`} inputPlaceholder={t('button_data')} textArea customPadding />
                </div>
              </Fragment>
            )}
          </SectionHeader>
          {!subSection && <PageSubSections sectionName={sectionName} />}
        </AccordionDetails>
      </Accordion>
      {deleteModalOpen && (
        <ModalContainer small>
          <DeleteModal
            id={deleteModalOpen}
            setModal={setDeleteModalOpen}
            route={deleteRoute}
            successMsg={'Section deleted successfully'}
            warningMsg={'Are you sure you want to delete this section?'}
          />
        </ModalContainer>
      )}
    </Fragment>
  );
}
